import { Button, Flex, Form, Input, Modal, Segmented } from "antd";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useSendOtpMutation } from "../../../app/features/auth/auth.api";
import { useGetAllPlansQuery } from "../../../app/features/onboarding/onboarding.api";
import type { Plan } from "../../../app/features/onboarding/onboarding.type";
import {
  AI_ADDONS,
  ENTERPRISE_PLAN,
  PRICING_PLANS,
  RECRUITMENT_MODULE_KEYWORD,
  type PricingPlanContent,
} from "../../../constants/pricing.constants";
import { CONTACT_PATH } from "../../../constants/company.constants";
import useDiscloser from "../../../hooks/useDiscloser";
import { CheckCircle } from "../../../icons/CheckCircle";
import cn from "../../../lib/cn";
import { formatNumber } from "../../../utils/number";
import SectionHeading from "./SectionHeading";

type BillingCycle = "monthly" | "yearly";

const formatBDT = (value: number) =>
  formatNumber(value, { currency: "BDT", decimals: 0 });

const AI_ROW = "ai-addons";

const normalize = (value?: string | null) => value?.trim().toLowerCase() ?? "";

type PricingCard = PricingPlanContent & {
  plan: Plan;
  moduleIds: Set<string>;
  hasAiAddons: boolean;
};

const SparkleIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z" />
    <path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8L19 15z" />
  </svg>
);

const FeatureRow = ({
  label,
  included,
}: {
  label: string;
  included: boolean;
}) => (
  <li
    className={cn("flex items-center gap-3 text-sm", !included && "opacity-30")}
  >
    {included ? (
      <CheckCircle className="text-theme-primary text-base flex-shrink-0" />
    ) : (
      <span className="flex w-4 justify-center flex-shrink-0">
        <span className="h-px w-2.5 bg-gray-400" />
      </span>
    )}
    <span className={included ? "text-gray-700" : "text-gray-500"}>
      {label}
    </span>
  </li>
);

const PlanCard = ({
  name,
  featured,
  price,
  subtitle,
  action,
  footnote,
  children,
}: {
  name: string;
  featured?: boolean;
  price: ReactNode;
  subtitle: string;
  action: ReactNode;
  footnote?: string;
  children: ReactNode;
}) => (
  <div
    className={cn(
      "group flex h-full flex-col rounded-2xl border p-7 transition-colors duration-300",
      featured
        ? "border-theme-primary/30 bg-gradient-to-b from-theme-primary/[0.07] to-white"
        : "border-gray-200 bg-white hover:border-theme-primary/40",
    )}
  >
    <h3 className="text-lg font-semibold text-gray-900">{name}</h3>
    <p className="mt-1 text-sm text-gray-500">{subtitle}</p>

    <div className="mt-6 flex h-12 items-baseline gap-1">{price}</div>

    <div className="mt-6">{action}</div>
    <p className="mt-2 h-4 text-center text-xs text-gray-400">{footnote}</p>

    <div className="my-6 h-px bg-gray-100" />

    <p className="mb-4 text-xs font-medium uppercase tracking-wider text-gray-400">
      Features
    </p>
    <ul className="flex-1 space-y-3">{children}</ul>
  </div>
);

const PricingSection = () => {
  const [form] = Form.useForm();
  const [config, handleOpen, handleClose] = useDiscloser();

  const [sendOtp, { isLoading: sending }] = useSendOtpMutation();

  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly");

  const isYearly = billingCycle === "yearly";

  // Monthly and yearly are separate plans (own id and price) from the API.
  const { data: plansResponse, isFetching: isLoading } = useGetAllPlansQuery({
    params: isYearly ? { is_yearly: true } : { is_monthly: true },
  });

  // Merge static presentation content with live plan data (id, price, users,
  // modules) by plan name, in the order defined by PRICING_PLANS.
  const apiPlans = plansResponse?.data || [];
  const cards = PRICING_PLANS.flatMap<PricingCard>((content) => {
    const plan = apiPlans.find((p) => normalize(p.name) === content.key);
    if (!plan) return [];
    return {
      ...content,
      plan,
      moduleIds: new Set(plan.planModules.map((m) => m.module_id)),
      hasAiAddons: plan.planModules.some((m) =>
        normalize(m.module.name).includes(RECRUITMENT_MODULE_KEYWORD),
      ),
    };
  });

  // Every module offered across plans, in tier order, so rows line up.
  const allModules = new Map<string, string>();
  cards.forEach(({ plan }) =>
    plan.planModules.forEach((m) => {
      if (!allModules.has(m.module_id)) {
        allModules.set(m.module_id, m.module.name);
      }
    }),
  );

  // Same rows on every card so they line up; Enterprise-only extras are shown
  // as unavailable on the self-serve plans.
  const featureRows: [string, string][] = [
    ...allModules,
    [AI_ROW, "AI Add-ons"],
    ...ENTERPRISE_PLAN.extras.map((extra): [string, string] => [extra, extra]),
  ];

  const aiPlanNames = [
    ...cards.filter((c) => c.hasAiAddons).map((c) => c.plan.name),
    ENTERPRISE_PLAN.name,
  ];

  return (
    <div className="bg-white py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-14 text-center">
          <SectionHeading
            eyebrow="Pricing"
            title="Simple, transparent pricing"
            // Free trial paused — original copy:
            // "Plans that grow with your team. Start with a 7-day free trial."
            description="Plans that grow with your team."
          />
          <Segmented
            size="large"
            className="mt-8"
            value={billingCycle}
            options={[
              { label: "Monthly", value: "monthly" },
              { label: "Yearly", value: "yearly" },
            ]}
            onChange={(value) => setBillingCycle(value as BillingCycle)}
          />
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {isLoading ? (
            [1, 2, 3, 4].map((index) => (
              <div
                key={index}
                className="rounded-2xl border border-gray-200 bg-white p-7"
              >
                <div className="h-5 w-24 bg-gray-100 rounded mb-3 animate-pulse" />
                <div className="h-4 w-20 bg-gray-100 rounded mb-6 animate-pulse" />
                <div className="h-10 w-32 bg-gray-100 rounded mb-6 animate-pulse" />
                <div className="h-10 w-full bg-gray-100 rounded-lg mb-10 animate-pulse" />
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((item) => (
                    <div
                      key={item}
                      className="h-4 bg-gray-100 rounded animate-pulse"
                    />
                  ))}
                </div>
              </div>
            ))
          ) : (
            <>
              {cards.map(({ plan, moduleIds, hasAiAddons }) => (
                <PlanCard
                  key={plan.id}
                  name={plan.name ?? ""}
                  price={
                    <>
                      <span className="text-4xl font-bold tracking-tight text-gray-900">
                        {formatBDT(Number(plan.price))}
                      </span>
                      <span className="text-sm text-gray-500">
                        {isYearly ? "/year" : "/month"}
                      </span>
                    </>
                  }
                  subtitle={`Up to ${plan.max_user.toLocaleString()} users`}
                  // Free trial paused — restore to show the trial under the button:
                  // footnote={
                  //   plan.trial_period > 0
                  //     ? `${plan.trial_period}-day free trial`
                  //     : undefined
                  // }
                  action={
                    <Button
                      block
                      size="large"
                      onClick={() => handleOpen({ type: "ACTION", data: plan })}
                      className="font-medium !border-gray-300 !text-gray-900 group-hover:!border-theme-primary group-hover:!bg-theme-primary group-hover:!text-white"
                    >
                      Get Started
                    </Button>
                  }
                >
                  {featureRows.map(([id, name]) => (
                    <FeatureRow
                      key={id}
                      label={name}
                      included={
                        moduleIds.has(id) || (id === AI_ROW && hasAiAddons)
                      }
                    />
                  ))}
                </PlanCard>
              ))}

              {/* Enterprise — not a self-serve plan, so it is fully static */}
              <PlanCard
                featured
                name={ENTERPRISE_PLAN.name}
                price={
                  <span className="text-4xl font-bold tracking-tight text-theme-primary">
                    Let&apos;s talk
                  </span>
                }
                subtitle="Custom users & pricing"
                footnote="Tailored to your organization"
                action={
                  <Link to={CONTACT_PATH} className="block">
                    <Button
                      block
                      size="large"
                      className="btn-primary font-medium"
                    >
                      Contact Sales
                    </Button>
                  </Link>
                }
              >
                {featureRows.map(([id, name]) => (
                  <FeatureRow key={id} label={name} included />
                ))}
              </PlanCard>
            </>
          )}
        </div>

        {/* AI Add-ons — gradient hairline border + soft glow */}
        <div className="mt-10 rounded-2xl bg-gradient-to-r from-theme-primary/60 via-theme-primary/10 to-theme-primary/60 p-px">
          <div className="relative overflow-hidden rounded-[15px] bg-white">
            <div className="pointer-events-none absolute -top-20 left-1/4 h-40 w-1/2 rounded-full bg-theme-primary/10 blur-3xl" />

            <div className="relative grid lg:grid-cols-[2fr_3fr]">
              <div className="p-8 lg:border-r lg:border-gray-100">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-theme-primary">
                  <SparkleIcon className="h-4 w-4" />
                  AI Add-ons
                </span>
                <h3 className="mt-3 text-2xl font-semibold tracking-tight text-gray-900">
                  Smarter hiring, pay as you go
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-500">
                  Add AI to any job post. No subscription — you only pay for
                  what you use.
                </p>
                <p className="mt-6 flex items-center gap-2 text-sm text-gray-700">
                  <CheckCircle className="text-theme-primary text-base" />
                  Available on {aiPlanNames.join(" & ")}
                </p>
              </div>

              <ul className="divide-y divide-gray-100 border-t border-gray-100 lg:border-t-0">
                {AI_ADDONS.map((addon) => (
                  <li
                    key={addon.name}
                    className="flex items-center justify-between gap-6 px-8 py-7"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{addon.name}</p>
                      <p className="mt-1 text-sm text-gray-500">
                        {addon.description}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-semibold tracking-tight text-gray-900">
                        {formatBDT(addon.price)}
                      </p>
                      <p className="text-xs text-gray-400">per {addon.unit}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <p className="mt-6 text-center text-sm text-gray-400">
          All prices are in BDT and billed {isYearly ? "yearly" : "monthly"}.
        </p>
      </div>

      <Modal
        footer={null}
        title={null}
        closable={false}
        open={config.open}
        onCancel={() => handleClose()}
      >
        <h1 className="text-3xl sm:text-4xl md:text-[40px] text-center font-bold text-gray-900">
          Start your Journey Here!
        </h1>
        <Form
          form={form}
          layout="vertical"
          onFinish={async (values) => {
            try {
              await sendOtp({ email: values.email }).unwrap();
              window.open(
                `${import.meta.env.VITE_HRX_ADMIN}/otp?email=${values.email}&planId=${config?.data?.id}&planType=${billingCycle}`,
                "_blank",
              );
              form.resetFields();
              handleClose();
            } catch (error) {
              console.error("Failed to send OTP:", error);
            }
          }}
          className="mt-10"
        >
          <Flex className="justify-between items-start gap-4">
            <Form.Item
              name="email"
              rules={[
                {
                  required: true,
                  message: "Please enter your email",
                },
                {
                  type: "email",
                  message: "Please enter a valid email address",
                },
              ]}
              className="flex-1 mb-0"
            >
              <Input
                size="large"
                placeholder="Enter email"
                className="!border-theme-slate-400 rounded"
              />
            </Form.Item>
            <Button
              size="large"
              htmlType="submit"
              loading={sending}
              className="btn-primary rounded"
            >
              Send Otp
            </Button>
          </Flex>
        </Form>
        <Flex gap={4} className="text-theme-gray-650 text-base mt-[30px]">
          Already have an account?{" "}
          <button
            onClick={() =>
              window.open(`${import.meta.env.VITE_HRX_ADMIN}/login`, "_blank")
            }
            className="text-theme-primary hover:text-theme-primary underline underline-offset-2"
          >
            Log in
          </button>
        </Flex>
      </Modal>
    </div>
  );
};

export default PricingSection;
